import i18n from '@/core/i18n';
import { BaseStatus, StatusConfig } from '@/modules/common';
import { createStatusOptionsMapper } from '@/modules/common/mappers/status-options.mapper';
import { ProjectStatus } from '@/modules/projects/types/project';

/**
 * Project status classes that implement the BaseStatus interface.
 * Each status knows how to configure itself for the StatusBadge component.
 */
export class ProjectStatusActive implements BaseStatus {
  getConfig(): StatusConfig {
    return {
      colorClasses: 'border border-green-600 text-green-600',
    };
  }
}

export class ProjectStatusTentative implements BaseStatus {
  getConfig(): StatusConfig {
    return {
      colorClasses: 'border border-orange-600 text-orange-600',
    };
  }
}

export class ProjectStatusArchived implements BaseStatus {
  getConfig(): StatusConfig {
    return {
      colorClasses: 'border border-muted-foreground text-muted-foreground',
    };
  }
}

/**
 * Badge-only synthetic project statuses — states the badge can render that are
 * not part of the ProjectStatus wire enum (the backend resolves no status for
 * a project that has not started yet).
 */
export enum ProjectBadgeSyntheticStatus {
  NotStarted = 'NOT_STARTED',
}

export class ProjectStatusNotStarted implements BaseStatus {
  getConfig(): StatusConfig {
    return {
      colorClasses: 'border border-blue-600 text-blue-600',
    };
  }
}

export class ProjectStatusUnkown implements BaseStatus {
  getConfig(): StatusConfig {
    return {
      colorClasses: 'border border-muted-foreground text-muted-foreground',
    };
  }
}

// Factory function to create status instances based on enum values
export const createProjectStatus = (status: string): BaseStatus => {
  switch (status) {
    case ProjectStatus.Active:
      return new ProjectStatusActive();
    case ProjectStatus.Tentative:
      return new ProjectStatusTentative();
    case ProjectStatus.Archived:
      return new ProjectStatusArchived();
    case ProjectBadgeSyntheticStatus.NotStarted:
      return new ProjectStatusNotStarted();
    default:
      return new ProjectStatusUnkown(); // Default fallback
  }
};

/**
 * Get localized status text for project status.
 */
export const getProjectStatusText = (status: ProjectStatus): string => {
  switch (status) {
    case ProjectStatus.Active:
      return i18n.t('authenticated.projects:status.active');
    case ProjectStatus.Tentative:
      return i18n.t('authenticated.projects:status.tentative');
    case ProjectStatus.Archived:
      return i18n.t('authenticated.projects:status.archived');
    default:
      return i18n.t('authenticated.projects:status.unknown');
  }
};

export const getProjectStatusColor = (status: ProjectStatus): string => {
  switch (status) {
    case ProjectStatus.Active:
      return 'green-600';
    case ProjectStatus.Tentative:
      return 'orange-600';
    case ProjectStatus.Archived:
      return 'muted-foreground';
    default:
      return 'muted-foreground';
  }
};

/**
 * Preconfigured mapper for project status options.
 * Maps an array of ProjectStatus values to option objects with label, value, and color.
 */
export const getProjectStatusOptions = createStatusOptionsMapper<ProjectStatus>({
  getStatusColor: getProjectStatusColor,
  getStatusText: getProjectStatusText,
});
